import api from './api';

/**
 * Get all departments
 * @returns {Promise} - API response with departments list
 */
export const getDepartments = async () => {
  try {
    const response = await api.get('/teacher/departments');
    return response.data;
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

/**
 * Get classes by department
 * @param {string|number} departmentId - ID of the department
 * @returns {Promise} - API response with classes list
 */
export const getClassesByDepartment = async (departmentId) => {
  try {
    const response = await api.get(`/teacher/departments/${departmentId}/classes`);
    return response.data;
  } catch (error) {
    console.error('Error fetching classes:', error);
    throw error;
  }
};

/**
 * Get teachers by class
 * @param {string|number} classId - ID of the class
 * @returns {Promise} - API response with teachers list
 */
export const getTeachersByClass = async (classId) => {
  try {
    const response = await api.get(`/teacher/classes/${classId}/teachers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching teachers:', error);
    throw error;
  }
};

/**
 * Create a new student
 * @param {Object} studentData - Student data to be created
 * @returns {Promise} - API response
 */
export const createStudent = async (studentData) => {
  try {
    const response = await api.post('/teacher/create-student', studentData);
    return response.data;
  } catch (error) {
    console.error('Error creating student:', error);
    throw error;
  }
};

/**
 * Get all students with pagination and search
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {string} params.search - Search query
 * @param {number} params.limit - Items per page
 * @returns {Promise} - API response
 */
export const getStudents = async ({ page = 1, search = '', limit = 10 } = {}) => {
  try {
    const response = await api.get('/teacher/students', {
      params: { page, search, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching students:', error);
    throw error;
  }
};

/**
 * Update a student
 * @param {number} id - Student ID (as query parameter)
 * @param {Object} studentData - Updated student data
 * @returns {Promise} - API response
 */
export const updateStudent = async (id, studentData) => {
  try {
    const response = await api.put('/teacher/update-student', studentData, {
      params: { id }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating student:', error);
    throw error;
  }
};

/**
 * Delete a student
 * @param {number} id - Database ID of the student (as query parameter)
 * @returns {Promise} - API response
 */
export const deleteStudent = async (id) => {
  try {
    const response = await api.delete('/teacher/delete-student', {
      params: { id }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting student:', error);
    throw error;
  }
};

/**
 * Update student status
 * @param {number} id - Database ID of the student (as query parameter)
 * @param {boolean} status - Status value (true or false)
 * @returns {Promise} - API response
 */
export const updateStudentStatus = async (id, status) => {
  try {
    const response = await api.put('/teacher/student-status-update', 
      { status },
      { params: { id } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating student status:', error);
    throw error;
  }
};

/**
 * Get classes taught by the authenticated teacher (filtered by timetable)
 * @returns {Promise} - API response with teacher's classes including section info
 */
export const getTeacherClasses = async (params = {}) => {
  try {
    const response = await api.get('/teacher/class-names', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching teacher classes:', error);
    throw error;
  }
};

const teacherService = { createStudent, getStudents, updateStudent, deleteStudent, updateStudentStatus, getTeacherClasses };
export default teacherService;
